import MainLayout from 'components/layout/MainLayout';
import BlogArticle from 'components/blog/BlogArticle';
import { ASSET_PREFIX } from 'utils/constants';
import { NextSeo } from "next-seo";
import Link from "next/link";
import { getCta } from 'utils/common';

const BestCastleStaysToLiveYourBestLifeArticle = () => {
  const IMAGE_DIRECTORY = `${ASSET_PREFIX}/images/blog/articles/best-castle-stays`;

  return (
    <MainLayout>
      <NextSeo
        title="Best Castle Stays To Live Your Best Life | DAYMADE"
        description="Fancy a luxurious overnight castle stay? We do too. Check out our winners' highlights and top picks!"
        openGraph={{
          title: 'Best Castle Stays To Live Your Best Life | DAYMADE',
          description:
            "Fancy a luxurious overnight castle stay? We do too. Check out our winners' highlights and top picks!",
          type: 'article',
          url: 'https://www.daymade.co.uk/blog/best-castle-stays-to-live-your-best-life',
          images: [
            {
              url: `${IMAGE_DIRECTORY}/hero.jpg`,
              alt: 'Couple kissing in gardens in front of Coombe Abbey at dusk',
            },
          ],
        }}
        // additionalMetaTags={[{
        //   name: 'keywords',
        //   content: 'win,travel,holiday,raffle,castle,castle stay,overnight,luxury,countryside,retreat,staycation,getaway,Shendish Manor,Coombe Abbey,Thornbury Castle,Shendish Manor,weekend,afternoon tea'
        // }]}
      />

      <BlogArticle
        imageDirectory={IMAGE_DIRECTORY}
        heroAltText="Fancy a luxurious overnight castle stay? We do too. Check out our winners' highlights and top picks!"
      >
        <>
          <h1 style={{ fontSize: '1.6em' }}>
            Best <strong>Castle Stays</strong> To Live Your Best Life
          </h1>

          <p className="subheading">
            Fancy a luxurious overnight castle stay? We do too. Check out our
            winners' highlights and top picks!
          </p>

          <div
            className="separator"
            style={{ width: '80%', marginTop: 0 }}
          />

          <p
            style={{
              fontStyle: 'italic',
              textAlign: 'center',
              marginBottom: '2em',
            }}
          >
            by Abbi James
          </p>

          <p>
            <span className="bigFirstLetter">L</span>et's face it, we've ALL
            wanted to stay in a castle at some point or another (
            <span style={{ fontStyle: 'italic' }}>
              don't deny it, we know you're lying!
            </span>
            ). Living that true princess life, or dining like a king?{' '}
            <strong>Sign. Us. Up.</strong>
          </p>

          <p>
            So how can we make this dream come true? Well, staying in a
            luxurious castle just so happens to be one of our most popular {''}
            <Link href="/prizes">
              <a style={{ margin: 0, fontWeight: 'bold' }}>£100 prizes</a>
            </Link>
            {''} from our {''}
            <Link href="/">
              <a style={{ margin: 0, fontWeight: 'bold' }}>Weekly Draws</a>
            </Link>
            {''}! And we can see why...
          </p>

          <p>
            If stepping back from the hustle and bustle of everyday life sounds
            like a dream to you (especially with everything going on right now),
            then an overnight stay in a beautiful castle is exactly what the
            doctor ordered. Never mind a little taste of luxury, we're talking
            luxury by the bucket-load.
          </p>

          <p>
            The UK is blessed with some absolutely stunning castles worthy of
            their spot in the history books.
          </p>

          <h4>
            Coombe <strong>Abbey</strong>
          </h4>

          <p>
            First on our list is Coombe Abbey, which can be traced back to 1150
            AD, and is set in 500 acres of stunning parkland.
          </p>

          <p>
            Our lovely DAYMADE winner, Monika, whisked her other half away for a
            luxury birthday retreat. With the chaos this year has brought to us
            all, what better way to unwind than to retreat to the calm of the
            countryside?
          </p>

          <p>
            <span style={{ fontStyle: 'italic' }}>
              “Coombe Abbey is absolutely stunning...while the hotel itself
              makes you feel like royalty!”
            </span>{' '}
            — Monika
          </p>

          <p>
            After a delicious 3-course dinner, Monika topped off the evening
            with some room service – but with a sparkling twist! Who doesn't
            love a bit of champers every now and then?
          </p>

          <p>
            <span style={{ fontStyle: 'italic' }}>
              “Coombe Abbey is definitely worth visiting if you are looking for
              relaxed and romantic weekend, it makes you forget about the stress
              of the world and feel like royalty!”
            </span>{' '}
            — Monika
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/hero.jpg`}
            alt="Fancy a luxurious overnight castle stay? We do too. Check out our winners' highlights and top picks!"
            className="singleBlogImage"
          />

          <p>
            Coombe Abbey is not just a glorious hotel for those who love to kick
            back and relax – it's oh so much more! With an 80-acre Site of
            Special Scientific Interest lake, conservation areas teeming with
            wildlife, and bird hides, you're sure to get back into nature in no
            time. For those who want to explore their wild side, you can even
            check out Go Ape within the park's woodland! Might be giving
            Tarzan's loincloth a miss though...
          </p>

          <p>
            Another one of our brilliant winners, Hannah, took her lovely mum on
            a castle stay adventure more recently. After being upgraded to a
            grand room of luxury, they decided to explore all there was to
            offer!
          </p>

          <p>
            <span style={{ fontStyle: 'italic' }}>
              “One of our highlights was being able to incorporate a localite
              prize we won and did some axe throwing within the grounds which
              myself and my mum were much better at than we thought we would
              be!”
            </span>{' '}
            — Hannah
          </p>

          <p>
            For those who love to explore the great outdoors, take a stroll by a
            meandering river, a history-filled moat or a lake teeming with
            nature. If you're even more adventurous, pack your (very
            sophisticated) wellies and take a trip through forests galore. Who
            knows where your Prince Charming might be wandering?
          </p>

          <div className="doubleImageContainer">
            <img
              src={`${IMAGE_DIRECTORY}/secondary1.jpg`}
              alt="One of our winners enjoying a delicious afternoon tea at Coombe Abbey"
              className="doubleBlogImage"
            />

            <img
              src={`${IMAGE_DIRECTORY}/secondary2.jpg`}
              alt="One of our players on a weekend getaway to a UK castle"
              className="doubleBlogImage"
            />
          </div>

          <h4>
            Thornbury <strong>Castle</strong>
          </h4>

          <p>
            If you're after a Tudor castle with the likes of a dungeon dining
            room, grand hall and open fireplaces, then Thornbury Castle has got
            you covered. There's no shortage of activities on offer, with both
            archery and falconry promising to keep you busy. For those who enjoy
            kicking back and relaxing, take in a spa treatment or two before
            strolling the kitchen gardens and 14 acres of stunning grounds.
          </p>

          <p>
            Great food speaks to the soul – and what better way to live the life
            of luxury/while away the afternoon than to enjoy afternoon tea on a
            sun-kissed terrace? Even better with a glass of something sparkling!
          </p>

          <p>
            Our lucky winner, Nicole, packed her bags to spend the weekend at
            Thornbury Castle, and her verdict was{' '}
            <span style={{ fontStyle: 'italic' }}>
              “It was just amazing! Thanks so much DAYMADE”
            </span>{' '}
            — we're super jealous, Nicole!
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary3.jpg`}
            alt="Couple standing outside the entrance to Thornbury Castle after winning a Weekender prize in one of our Weekly Draws"
            className="singleBlogImage"
            style={{
              maxWidth: '400px',
            }}
          />

          <p>
            When it comes to sleeping arrangements, who HASN'T dreamed of their
            perfect four-poster bed? Soft feather pillows, silky bedlinen, and a
            dreamy sheer curtain (you know the one)... Even better if it has a
            bell for room service! Although this might not be at the top of
            everyone's necessities, a good hearty breakfast ought to be. None of
            that single-piece-of-buttered-toast nonsense, oh no. When you stay
            in a beautiful castle, your breakfast ought to be second to none. We
            always make sure our castle stay winners start their day right with
            a beautifully-cooked breakfast in a stunning courtyard, balcony,
            jaw-dropping dining hall or the like.
          </p>

          <h4>
            Shendish <strong>Manor</strong>
          </h4>

          <p>
            For those who love a spot of golf on your hols, then Shendish Manor
            is the place to BE! Golf might not be my forte, but ahh what I
            wouldn't give to have free reign over a golf buggy...
          </p>

          <p>
            The fabulous Phoebe checked herself into this Victorian wonderland —
            a little getaway to escape the chaos we’ve all been surrounded by as
            of late. Dinner on the terrace overlooking formal Dutch Gardens?
            We'll be here all night, thanks!
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary4.jpg`}
            alt="One of our Weekender winners overlooking the green gardens by Shendish Manor"
            className="singleBlogImage"
            style={{
              maxWidth: '400px',
            }}
          />

          <br />

          <p>
            How about you? Would you agree with our previous winners and choose
            one of the above, or do you have somewhere else in mind? Whatever
            your dream castle stay might be, you can win it with DAYMADE. {''}
            <Link href={getCta()?.url}>
              <a style={{ margin: 0, fontWeight: 'bold' }}>Enter now</a>
            </Link>
            {''}!
          </p>
        </>
      </BlogArticle>
    </MainLayout>
  );
};

export default BestCastleStaysToLiveYourBestLifeArticle;
